import React, { useState, useEffect } from 'react';
import './Team.css';
import { getContractReadOnly } from '../utils/web3';
import { formatUserId, copyToClipboard } from '../utils/formatters';
import { PACKAGE_INFO } from '../config/contract';

const Team = ({ userId, userData, packages, referrals }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [levelCounts, setLevelCounts] = useState([]);
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [levelMembers, setLevelMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [copied, setCopied] = useState(false);
  
  const membersPerPage = 10;
  const activePackages = packages.filter(p => p.isActive);

  useEffect(() => {
    if (activePackages.length > 0 && !selectedPackage) {
      setSelectedPackage(activePackages[0].id);
    }
  }, [activePackages]);

  useEffect(() => {
    if (selectedPackage) {
      fetchLevelCounts();
    }
  }, [selectedPackage]);

  const fetchLevelCounts = async () => {
    try {
      const contract = getContractReadOnly();
      const counts = await contract.getMatrixLevelCounts(userId, selectedPackage);
      setLevelCounts(counts.map(c => c.toNumber()));
    } catch (error) {
      console.error('Error fetching level counts:', error);
      setLevelCounts([]);
    }
  };

  const fetchLevelMembers = async (level) => {
    setIsLoadingMembers(true);
    try {
      const contract = getContractReadOnly();
      const members = await contract.getMatrixLevelUsers(userId, selectedPackage, level);
      setLevelMembers(members.map(m => m.toNumber()));
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching level members:', error);
      setLevelMembers([]);
    } finally {
      setIsLoadingMembers(false);
    }
  };

  const handleLevelClick = (level) => {
    if (expandedLevel === level) {
      setExpandedLevel(null);
      setLevelMembers([]);
    } else {
      setExpandedLevel(level);
      fetchLevelMembers(level);
    }
  };

  const handleCopyReferral = async () => {
    const link = `${window.location.origin}?ref=${userId}`;
    const success = await copyToClipboard(link);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCopyId = async (id) => {
    await copyToClipboard(id.toString());
  };

  const totalTeam = levelCounts.reduce((a, b) => a + b, 0);
  const paginatedMembers = levelMembers.slice(
    (currentPage - 1) * membersPerPage,
    currentPage * membersPerPage
  );
  const totalPages = Math.ceil(levelMembers.length / membersPerPage);

  return (
    <div className="team-page">
      <div className="team-header animate-fadeInUp">
        <img src="/assets/gifs/6.gif" alt="Packages" className="header-gif" />
        <h2>Your Team</h2>
        <p>Matrix structure across all levels</p>
      </div>

      {/* Stats Overview */}
      <div className="team-stats animate-fadeInUp delay-100">
        <div className="team-stat-card">
          <span className="stat-icon">🎯</span>
          <div className="stat-info">
            <span className="stat-num">{userData.totalDirectReferrals}</span>
            <span className="stat-label">Direct</span>
          </div>
        </div>
        <div className="team-stat-card">
          <span className="stat-icon">🌐</span>
          <div className="stat-info">
            <span className="stat-num">{totalTeam}</span>
            <span className="stat-label">Total Team</span>
          </div>
        </div>
      </div>

      {/* Referral Link */}
      <section className="referral-section animate-fadeInUp delay-200">
        <div className="section-title">📎 Referral Link</div>
        <div className="referral-box">
          <input 
            type="text" 
            value={`${window.location.origin}?ref=${userId}`}
            readOnly 
            className="referral-input"
          />
          <button 
            className={`copy-btn ${copied ? 'copied' : ''}`} 
            onClick={handleCopyReferral}
          >
            {copied ? '✓' : '📋'}
          </button>
        </div>
      </section>

      {/* Package Selector */}
      <section className="package-selector animate-fadeInUp delay-300">
        <div className="section-title">📦 Select Unit</div>
        <div className="select-wrapper">
          <select 
            value={selectedPackage || ''} 
            onChange={(e) => {
              setSelectedPackage(parseInt(e.target.value));
              setExpandedLevel(null);
              setLevelMembers([]);
            }}
            className="package-select"
          >
            {activePackages.map(pkg => (
              <option key={pkg.id} value={pkg.id}>
                {PACKAGE_INFO[pkg.id].icon} {PACKAGE_INFO[pkg.id].name} - ${PACKAGE_INFO[pkg.id].price}
              </option>
            ))}
          </select>
          <span className="select-arrow">▼</span>
        </div>
      </section>

      {/* Levels List */}
      <section className="levels-section animate-fadeInUp delay-400">
        <div className="section-title">📊 Level Structure</div>
        
        <div className="levels-list">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => {
            const count = levelCounts[level - 1] || 0;
            const isExpanded = expandedLevel === level;
            
            return (
              <div key={level} className={`level-item ${isExpanded ? 'expanded' : ''}`}>
                <button 
                  className="level-header"
                  onClick={() => count > 0 && handleLevelClick(level)}
                  disabled={count === 0}
                >
                  <div className="level-info">
                    <span className="level-badge">L{level}</span>
                    <span className="level-name">Level {level}</span>
                  </div>
                  <div className="level-count">
                    <span className="count-num">{count}</span>
                    <span className="count-label">members</span>
                  </div>
                  {count > 0 && (
                    <span className={`expand-icon ${isExpanded ? 'rotated' : ''}`}>▼</span>
                  )}
                </button>

                {isExpanded && (
                  <div className="level-members">
                    {isLoadingMembers ? (
                      <div className="members-loading">
                        <span className="loading-spinner"></span>
                        <span>Loading members...</span>
                      </div>
                    ) : (
                      <>
                        <div className="members-list">
                          {paginatedMembers.map((memberId) => (
                            <div key={memberId} className="member-item">
                              <span className="member-icon">👤</span>
                              <span className="member-id">{formatUserId(memberId)}</span>
                              <button 
                                className="member-copy"
                                onClick={() => handleCopyId(memberId)}
                              >
                                📋
                              </button>
                            </div>
                          ))}
                        </div>

                        {totalPages > 1 && (
                          <div className="pagination">
                            <button 
                              className="page-btn"
                              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                              disabled={currentPage === 1}
                            >
                              ←
                            </button>
                            <span className="page-info">
                              {currentPage} / {totalPages}
                            </span>
                            <button 
                              className="page-btn"
                              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                              disabled={currentPage === totalPages}
                            >
                              →
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Direct Referrals */}
      <section className="direct-section animate-fadeInUp delay-500">
        <div className="section-title">🎯 Direct Referrals</div>
        
        {referrals.length > 0 ? (
          <div className="direct-list">
            {referrals.slice(0, 10).map((refId) => (
              <div key={refId} className="direct-item">
                <span className="direct-icon">👤</span>
                <span className="direct-id">{formatUserId(refId)}</span>
                <button 
                  className="direct-copy"
                  onClick={() => handleCopyId(refId)}
                >
                  📋
                </button>
              </div>
            ))}
            {referrals.length > 10 && (
              <div className="more-hint">
                +{referrals.length - 10} more referrals
              </div>
            )}
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-icon">👥</span>
            <p>No direct referrals yet</p>
            <span className="empty-hint">Share your referral link to grow your team</span>
          </div>
        )}
      </section>
    </div>
  );
};

export default Team;
